import Joi from 'joi';

import {
  type ForgotPasswordRequest,
  type ResetPasswordRequest,
  type SigninRequest,
} from '@/api/routes/public/auth.api';
import { type SignupFormData } from '@/domain/auth/types';
import { CommonValidationSchemas } from '@/utils/schemas';
import { translatedMessages } from '@/i18n';

export const signinSchema = Joi.object<SigninRequest>().keys({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'emailRequired',
        'string.email': 'emailInvalid',
        'any.required': 'emailRequired',
      }),
    ),
  password: Joi.string()
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'passwordRequired',
        'any.required': 'passwordRequired',
      }),
    ),
});

export const signupSchema = Joi.object<SignupFormData>().keys({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'emailRequired',
        'string.email': 'emailInvalid',
        'any.required': 'emailRequired',
      }),
    ),
  firstName: Joi.string()
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'firstNameRequired',
        'any.required': 'firstNameRequired',
      }),
    ),
  lastName: Joi.string().allow(null, '').optional(),
  password: CommonValidationSchemas.password,
  confirmPassword: Joi.string()
    .required()
    .valid(Joi.ref('password'))
    .error(
      translatedMessages('domain.auth.schemas', {
        'any.only': 'passwordMismatch',
        'string.empty': 'confirmPasswordRequired',
        'any.required': 'confirmPasswordRequired',
      }),
    ),
  acceptPrivacyPolicy: Joi.boolean()
    .valid(true)
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'any.only': 'privacyRequired',
        'any.required': 'privacyRequired',
      }),
    ),
  receivePlatformUpdateEmails: Joi.boolean().default(false),
});

export const forgotPasswordSchema = Joi.object<ForgotPasswordRequest>({
  email: Joi.string()
    .email({ tlds: false })
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'emailRequiredAlt',
        'string.email': 'emailInvalidShort',
      }),
    ),
});

export const resetPasswordSchema = Joi.object<ResetPasswordRequest & { confirmPassword: string }>({
  token: Joi.string()
    .required()
    .error(
      translatedMessages('domain.auth.schemas', {
        'string.empty': 'tokenRequired',
        'any.required': 'tokenRequired',
      }),
    ),
  newPassword: CommonValidationSchemas.password,
  confirmPassword: Joi.string()
    .required()
    .valid(Joi.ref('newPassword'))
    .error(
      translatedMessages('domain.auth.schemas', {
        'any.only': 'passwordMismatch',
        'string.empty': 'confirmPasswordRequired',
        'any.required': 'confirmPasswordRequired',
      }),
    ),
});
